import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
// import { AvatarStack } from '@/components/custom/avatar/avatar';
import { PiStudent } from 'react-icons/pi';
import { FiStar, FiChevronDown } from 'react-icons/fi';
import { TbCopy } from 'react-icons/tb';
import toast from 'react-hot-toast';
import { useClient } from 'jsonapi-react';
import { confirm } from '@/lib/confirm';
import { useNavigate } from 'react-router-dom';
import { t } from 'i18next';
import { useFormik } from 'formik';
import { ModalWarning } from '@/components/custom/modal/modal-warning';
import usePermission from '@/hooks/usePermission';
import promiseRequest from '@/utils/promiseToast';
import { IconVerified } from '@/components/custom/icon';
import { useSession } from '@/hooks/useSession';
import usePlayfulTeacher from '@/hooks/usePlayfulTeacher';
import { PlayfulCard, PlayfulCardButton } from '@/components/custom/playful-card';
import {
  CourseCardAdmin,
  CourseCardAdminBody,
  CourseCardAdminContent,
  CourseCardAdminFooter,
  CourseCardAdminHeader,
  CourseCardAdminHeaderContent,
  CourseCardAdminImage,
  CourseCardAdminImageWrapper,
  CourseCardAdminMetric,
  CourseCardAdminMetrics,
  CourseCardAdminRating,
  CourseCardAdminTitle
} from '@/components/custom/course';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';

export default function CourseAdminCard(props) {
  const navigate = useNavigate();
  const { course, setFilteredCourses, handleOpenModulesModal } = props;
  const client = useClient();
  const [showWarning, setShowWarning] = useState(false);
  const [showPublishWarning, setShowPublishWarning] = useState(false);

  const { promiseToast, isSubmitting } = promiseRequest();

  const handleDelete = async () => {
    const confirmed = await confirm({
      title: t('projects.warningDeleteCourse'),
      confirmText: t('button.yes'),
      cancelText: t('button.no')
    });
    if (confirmed) {
      deleteCourse();
    }
  };

  const courseForm = useFormik({
    initialValues: {
      published: course?.published
    }
  });

  useEffect(() => {
    courseForm.setFieldValue('published', course?.published);
  }, [course.published]);

  const deleteCourse = async () => {
    const { error } = await client.delete(['/courses', course.id]);
    if (error) {
      toast.error('Erro ao excluir curso');
    } else {
      toast.success('Curso excluído!');
      setFilteredCourses(prevState => prevState.filter(item => item.id !== course.id));
    }
  };

  const updateCoursePublicity = async values => {
    promiseToast({
      url: `courses/${course.id}`,
      request: {
        published: !values.published
      },
      successText: !courseForm.values.published ? 'Publicado com sucesso!' : 'Despublicado com sucesso!',
      errorText: 'Erro ao publicar curso'
    }).then(() => {
      courseForm.setFieldValue('published', !courseForm.values.published);
      setShowWarning(false);
      setShowPublishWarning(false);
    });
  };

  const handlePublishCourse = () => {
    if (course?.['learning-systems-count'] === 0 && !courseForm.values.published) {
      setShowWarning(true);
    } else if (courseForm.values.published) {
      setShowPublishWarning(true);
    } else {
      updateCoursePublicity(courseForm.values);
    }
  };

  const currentUser = useSession()?.session?.user;

  // Hooks não podem ser chamados dentro do JSX condicional (o menu só renderiza
  // em alguns casos, e isso mudava a ordem dos hooks entre renders). Todas as
  // permissões são resolvidas aqui em cima, incondicionalmente.
  const canPublish = usePermission('Gerenciar conteúdo', 'Cursos');
  const canEdit = usePermission('Editar cursos', 'Cursos');
  const canDelete = usePermission('Excluir cursos', 'Cursos');

  const isCreatorOrAdmin = !course?.['authored-by-user'] || (course?.['authored-by-user'] && currentUser?.id === course?.['creator-id']) || currentUser?.profileNames.includes('Administrador');

  const isPublished = courseForm.values.published;
  // Publicar saiu do switch e virou item do menu, então o menu também precisa
  // aparecer para quem só tem a permissão de publicar — senão essa pessoa perde
  // o acesso que tinha antes.
  const showActionsMenu = canEdit || canDelete || (canPublish && isCreatorOrAdmin);

  const playful = usePlayfulTeacher();
  const CardWrapper = playful ? PlayfulCard : CourseCardAdmin;

  const playfulCardStyle = playful ? { background: 'linear-gradient(180deg, #F4FDFF 0%, #F2FCFF 45%, #FFFCF1 100%)' } : undefined;

  return (
    <CardWrapper
      className={playful ? 'group overflow-hidden' : undefined}
      style={playfulCardStyle}
    >
      {/* Com 3 colunas o card fica mais largo, e a imagem em aspect-[16/9]
          crescia junto (herda a largura). Altura fixa no playful trava o
          tamanho da imagem independente da largura do card. */}
      <CourseCardAdminImageWrapper className={playful ? '!aspect-auto !h-[180px]' : undefined}>
        <CourseCardAdminImage
          src={course?.image?.url}
          alt=""
        />
        <Badge
          size="sm"
          square
          variant={isPublished ? 'success-soft' : 'muted'}
          className={`absolute top-3 z-[1] ${playful ? 'right-3' : 'left-3'}`}
        >
          {isPublished ? t('textsCommon.published') : t('textsCommon.unpublished')}
        </Badge>
      </CourseCardAdminImageWrapper>

      <CourseCardAdminContent>
        <CourseCardAdminHeader>
          <CourseCardAdminHeaderContent>
            {course?.['official-content'] && <IconVerified />}
            <CourseCardAdminTitle>{course?.title}</CourseCardAdminTitle>
          </CourseCardAdminHeaderContent>
        </CourseCardAdminHeader>

        <CourseCardAdminBody>
          <CourseCardAdminMetrics>
            <CourseCardAdminMetric
              icon={PiStudent}
              label={t('projects.titleStudents')}
              value={course?.['student-count']}
            />
            <CourseCardAdminMetric
              icon={TbCopy}
              label={t('textsCommon.publishedModules')}
              value={course?.['learning-systems-count']}
              onClick={() => handleOpenModulesModal(course)}
            />
          </CourseCardAdminMetrics>
        </CourseCardAdminBody>

        <CourseCardAdminFooter>
          {!playful && (
            <CourseCardAdminRating>
              <FiStar /> {parseFloat(course?.rating).toFixed(1)} <span>({course?.['total-reviews']})</span>
            </CourseCardAdminRating>
          )}

          <div className={playful ? 'flex w-full items-center gap-2' : 'ml-auto flex flex-none items-center gap-2'}>
            {playful ? (
              <PlayfulCardButton
                className="h-9 flex-1 px-5"
                onClick={() => navigate(`/curso/admin/${course?.id}`)}
              >
                {t('button.view')}
              </PlayfulCardButton>
            ) : (
              <Button
                size="sm"
                asChild
                className="rounded-full px-5"
              >
                <Link to={`/curso/admin/${course?.id}`}>{t('button.view')}</Link>
              </Button>
            )}

            {showActionsMenu && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  {playful ? (
                    <PlayfulCardButton
                      variant="outline"
                      aria-label={t('button.options')}
                      className="h-9 w-9 flex-none gap-0 px-0"
                    >
                      <FiChevronDown size={20} />
                    </PlayfulCardButton>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      aria-label={t('button.options')}
                      className="aspect-square rounded-full px-0"
                    >
                      <FiChevronDown size={20} />
                    </Button>
                  )}
                </DropdownMenuTrigger>

                <DropdownMenuContent align="end">
                  {canPublish && isCreatorOrAdmin && <DropdownMenuItem onClick={handlePublishCourse}>{isPublished ? t('lms.unpublish') : t('lms.publish')}</DropdownMenuItem>}
                  {isCreatorOrAdmin && canEdit && (
                    <DropdownMenuItem
                      onClick={() => {
                        navigate(`/curso/criar/sobre/${course?.id}`);
                      }}
                    >
                      {t('button.edit')}
                    </DropdownMenuItem>
                  )}
                  {isCreatorOrAdmin && canDelete && <DropdownMenuItem onClick={handleDelete}>{t('button.delete')}</DropdownMenuItem>}
                  {isCreatorOrAdmin && (
                    <DropdownMenuItem asChild>
                      <Link to={`/curso/admin/gerenciar/${course?.id}/m/disciplinas-e-aulas/d`}>{t('button.manageContent')}</Link>
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </CourseCardAdminFooter>
      </CourseCardAdminContent>

      <ModalWarning
        isSubmitting={isSubmitting}
        show={showWarning}
        titleFirstButton="Confirmo que quero publicar"
        titleSecondButton="Manter despublicado"
        onClose={() => setShowWarning(false)}
        title={'Você tem certeza que deseja publicar o curso sem módulos e aulas publicados?'}
        description={'Ele aparecerá sem conteúdo pois não há módulos e aulas publicados para os estudantes.'}
        onSubmitFirstButton={() => updateCoursePublicity(courseForm.values)}
        onSubmitSecondButton={() => setShowWarning(false)}
      />

      <ModalWarning
        isSubmitting={isSubmitting}
        show={showPublishWarning}
        titleFirstButton="Confirmo que quero despublicar"
        titleSecondButton="Manter publicado"
        onClose={() => setShowPublishWarning(false)}
        title={'Ao despublicar, este conteúdo ficará invisível para os alunos.'}
        description={'Você pode publicá-lo novamente a qualquer momento. Deseja prosseguir com a despublicação?'}
        onSubmitFirstButton={() => updateCoursePublicity(courseForm.values)}
        onSubmitSecondButton={() => setShowPublishWarning(false)}
      />
    </CardWrapper>
  );
}
